import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Alert, Button, Card, Empty, Progress, Segmented, Skeleton, Tooltip, message } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PhoneOutlined,
  ReloadOutlined,
  RetweetOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  WarningOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { Area, AreaChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import HourlyHeatmapSection from '../../components/call-center/HourlyHeatmapSection';
import RepeatCallersSection from '../../components/call-center/RepeatCallersSection';
import CallSubjectChip from '../../components/call-center/subjects/CallSubjectChip';
import PersianDatePicker from '../../components/PersianDatePicker';
import { callCenterReportsApi } from '../../utils/api';
import { toGregorian } from '../../utils/dateUtils';
import { parseDateRangeChange, getDateRangePickerValue } from '../reports/reportUtils';

const JALALI_FORMAT = 'jYYYY/jMM/jDD';

const toApiDate = (jalaliStr) => {
  if (!jalaliStr) return null;
  return toGregorian(String(jalaliStr).replace(/-/g, '/'), 'YYYY-MM-DD');
};

const rangeForLastDays = (days) => ({
  startDate: moment().subtract(days - 1, 'days').format(JALALI_FORMAT),
  endDate: moment().format(JALALI_FORMAT),
});

const PRESETS = [
  { label: 'امروز', value: 1 },
  { label: '۷ روز اخیر', value: 7 },
  { label: '۳۰ روز اخیر', value: 30 },
];

const WEEKDAYS_FA = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];

// برچسب تولتیپ نمودار: روز هفته + تاریخ شمسی (داده‌ها روزانه تجمیع شده‌اند)
const formatChartDate = (d) => {
  const m = moment(d, 'YYYY-MM-DD');
  return `${WEEKDAYS_FA[m.day()]} ${m.format(JALALI_FORMAT)}`;
};

const formatCount = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const formatPercent = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`);
const formatDecimal = (v, digits = 1) => (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: digits }));
const formatSeconds = (v) => {
  if (v == null) return '—';
  const s = Math.round(Number(v) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * good: جهت مطلوب تغییر (up = افزایش خوب است، down = کاهش خوب است، neutral = بدون قضاوت)
 */
const KPI_CONFIG = [
  { key: 'total_calls', label: 'کل تماس‌ها', format: formatCount, good: 'neutral', icon: <PhoneOutlined />, color: '#0d9488', hint: 'مجموع تماس‌های ورودی و خروجی' },
  { key: 'incoming', label: 'تماس ورودی', format: formatCount, good: 'neutral', icon: <ArrowDownOutlined />, color: '#2563eb', hint: 'تماس‌هایی که مشتری گرفته است' },
  { key: 'outgoing', label: 'تماس خروجی', format: formatCount, good: 'neutral', icon: <ArrowUpOutlined />, color: '#16a34a', hint: 'تماس‌هایی که اپراتور گرفته است' },
  { key: 'answer_rate', label: 'نرخ پاسخگویی', format: formatPercent, good: 'up', icon: <CheckCircleOutlined />, color: '#059669', hint: 'درصد تماس‌های ورودی که پاسخ داده شده‌اند' },
  { key: 'missed', label: 'تماس بی‌پاسخ', format: formatCount, good: 'down', icon: <StopOutlined />, color: '#dc2626', hint: 'تماس‌های ورودی بی‌پاسخ' },
  { key: 'avg_wait', label: 'میانگین زمان انتظار', format: formatSeconds, good: 'down', icon: <ClockCircleOutlined />, color: '#d97706', hint: 'میانگین انتظار مشتری تا پاسخ (دقیقه:ثانیه)' },
  { key: 'aht', label: 'میانگین زمان رسیدگی', format: formatSeconds, good: 'down', icon: <FieldTimeOutlined />, color: '#7c3aed', hint: 'میانگین مکالمه + ACW (دقیقه:ثانیه)' },
  { key: 'avg_talk', label: 'میانگین زمان مکالمه', format: formatSeconds, good: 'neutral', icon: <CustomerServiceOutlined />, color: '#0891b2', hint: 'میانگین مدت مکالمه تماس‌های پاسخ‌داده‌شده' },
  { key: 'acw', label: 'زمان کار پس از تماس', format: formatSeconds, good: 'down', icon: <ThunderboltOutlined />, color: '#ea580c', hint: 'تخمین زمان کار پس از تماس: فاصله پایان تماس تا شروع تماس بعدی اپراتور (حداکثر ۵ دقیقه)' },
  { key: 'sla', label: 'پاسخگویی به‌موقع (SLA)', format: formatPercent, good: 'up', icon: <SafetyCertificateOutlined />, color: '#4f46e5', hint: 'درصد تماس‌های ورودی که ظرف ۲۰ ثانیه پاسخ داده شده‌اند' },
  { key: 'callback_rate', label: 'تماس برگشتی', format: formatPercent, good: 'up', icon: <RetweetOutlined />, color: '#0284c7', hint: 'درصد تماس‌های بی‌پاسخ که ظرف ۲۴ ساعت با همان شماره تماس گرفته شده است' },
  { key: 'conversion', label: 'نرخ تبدیل', format: formatPercent, good: 'up', icon: <RiseOutlined />, color: '#c026d3', hint: 'درصد تماس‌های پاسخ‌داده‌شده که به یک معامله متصل هستند' },
  { key: 'avg_operator_score', label: 'میانگین امتیاز اپراتور', format: (v) => (v == null ? '—' : `${formatDecimal(v)} / 100`), good: 'up', icon: <StarOutlined />, color: '#d97706', hint: 'میانگین امتیاز ۰ تا ۱۰۰ که هوش مصنوعی به عملکرد اپراتورها داده است' },
  { key: 'fcr_rate', label: 'حل در تماس اول (FCR)', format: formatPercent, good: 'up', icon: <CheckCircleOutlined />, color: '#0f766e', hint: 'درصد تماس‌هایی که مشکل مشتری در همان تماس حل شده است (تشخیص AI)' },
];

const ChangeBadge = ({ change, value, previous, good }) => {
  // بازه قبل داده‌ای نداشته (null) یا مقدار قبلی صفر بوده و درصد تغییر تعریف نمی‌شود
  if (change == null) {
    if (previous === 0 && Number(value) > 0) {
      const tone = good === 'down' ? 'text-red-600' : good === 'up' ? 'text-emerald-600' : 'text-gray-500';
      return (
        <span className={`custom-font text-xs font-medium inline-flex items-center gap-1 ${tone}`}>
          <ArrowUpOutlined /> جدید
        </span>
      );
    }
    return <span className="custom-font text-xs text-gray-300">—</span>;
  }
  const up = change > 0;
  const flat = change === 0;
  let tone = 'text-gray-500';
  if (!flat && good !== 'neutral') {
    const isGood = (good === 'up') === up;
    tone = isGood ? 'text-emerald-600' : 'text-red-600';
  }
  return (
    <span className={`custom-font text-xs font-medium inline-flex items-center gap-1 ${tone}`} dir="ltr">
      {flat ? <MinusOutlined /> : up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {Math.abs(change).toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  );
};

const Sparkline = ({ series, color, gradientId, onHover }) => {
  const data = (series || []).map((p) => ({ date: p.date, value: p.value ?? 0 }));
  if (data.length < 2) return <div className="h-10" />;
  return (
    <div className="h-10 w-full" dir="ltr">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 2, right: 0, bottom: 2, left: 0 }}
          onMouseMove={(state) => {
            // Recharts v3: activePayload حذف شده و فقط ایندکس نقطه فعال داده می‌شود
            const idx = Number(state?.activeTooltipIndex);
            onHover?.(Number.isInteger(idx) && data[idx] ? data[idx] : null);
          }}
          onMouseLeave={() => onHover?.(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" hide />
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <ChartTooltip
            cursor={{ stroke: color, strokeOpacity: 0.3 }}
            content={() => null}
          />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${gradientId})`} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const KpiCard = ({ config, kpi }) => {
  const { key, label, format, good, icon, color, hint } = config;
  // نقطه زیر ماوس: مقدار و تاریخ/بازه زمانی آن در همان کارت نمایش داده می‌شود (تولتیپ ممکن است زیر کارت‌ها کلیپ شود)
  const [hovered, setHovered] = useState(null);
  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-4 flex flex-col gap-2 min-w-0">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0"
            style={{ background: `${color}1f`, color }}
          >
            {icon}
          </span>
          <span className="custom-font text-sm font-medium text-gray-600 dark:text-gray-300 truncate">{label}</span>
        </div>
        <Tooltip title={<span className="custom-font">{hint}</span>}>
          <InfoCircleOutlined className="text-gray-300 hover:text-gray-500 cursor-help shrink-0" />
        </Tooltip>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div className="custom-font text-2xl font-bold text-gray-900 dark:text-white leading-tight" dir="ltr">
          {format(kpi?.value)}
        </div>
        <div className="flex flex-col items-end">
          <ChangeBadge change={kpi?.change} value={kpi?.value} previous={kpi?.previous} good={good} />
          {kpi?.previous != null && (
            <span className="custom-font text-[11px] text-gray-400" dir="ltr">
              قبل: {format(kpi.previous)}
            </span>
          )}
        </div>
      </div>

      <div className="custom-font text-[11px] h-4 leading-4 truncate" style={{ color: hovered ? color : undefined }}>
        {hovered ? (
          <>
            {formatChartDate(hovered.date)} · ۰۰:۰۰ تا ۲۳:۵۹ ·{' '}
            <b dir="ltr" className="inline-block">{format(hovered.value)}</b>
          </>
        ) : null}
      </div>
      <Sparkline series={kpi?.series} color={color} gradientId={`spark-${key}`} onHover={setHovered} />
    </div>
  );
};

const CallCenterDashboard = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState(7);
  const [dateRange, setDateRange] = useState(() => rangeForLastDays(7));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const apiFrom = toApiDate(dateRange.startDate);
  const apiTo = toApiDate(dateRange.endDate);

  const load = useCallback(async (refresh = false) => {
    if (!apiFrom || !apiTo) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callCenterReportsApi.getDashboard({ from: apiFrom, to: apiTo, refresh });
      setData(res?.data ?? null);
    } catch (err) {
      console.error('خطا در دریافت داشبورد مرکز تماس:', err);
      setError(err?.message || 'خطا در دریافت داشبورد مرکز تماس');
      message.error(err?.message || 'خطا در دریافت داشبورد مرکز تماس');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiFrom, apiTo]);

  useEffect(() => {
    const timer = setTimeout(() => load(false), 300);
    return () => clearTimeout(timer);
  }, [load]);

  const handlePreset = (days) => {
    setPreset(days);
    setDateRange(rangeForLastDays(days));
  };

  const hasCalls = useMemo(() => (data?.kpis?.total_calls?.value ?? 0) > 0, [data]);

  return (
    <MainLayout>
      <div className="custom-font w-full max-w-full overflow-x-hidden" style={{ fontFamily: 'CustomFont, sans-serif', minWidth: 0 }}>
        <Card className="shadow-xl border-0 custom-font w-full max-w-full" bodyStyle={{ padding: 0, overflow: 'hidden' }}>
          {/* نوار عنوان */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <DashboardOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 custom-font">داشبورد مدیریتی مرکز تماس</h1>
                <div className="text-gray-500 dark:text-gray-400 text-sm custom-font">
                  شاخص‌های کلیدی عملکرد در مقایسه با بازه قبلی هم‌طول
                </div>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(true)} className="custom-font">
              بروزرسانی
            </Button>
          </div>

          <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-4">
            {/* فیلترها */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-3 md:p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <Segmented
                  value={PRESETS.some((p) => p.value === preset) ? preset : undefined}
                  options={PRESETS}
                  onChange={handlePreset}
                  className="custom-font"
                />
                <div className="w-full lg:w-72">
                  <PersianDatePicker
                    range
                    value={getDateRangePickerValue(dateRange.startDate, dateRange.endDate)}
                    onChange={(dates) => {
                      const { startDate, endDate } = parseDateRangeChange(dates);
                      if (startDate && endDate) {
                        setPreset(null);
                        setDateRange({ startDate, endDate });
                      }
                    }}
                    placeholder="انتخاب بازه تاریخ"
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    allowClear={false}
                  />
                </div>
              </div>
              {data && (
                <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 custom-font flex flex-wrap gap-x-4 gap-y-1" dir="rtl">
                  <span>بازه جاری: <span dir="ltr">{data.from?.slice(0, 10)} تا {data.to?.slice(0, 10)}</span> ({data.days} روز)</span>
                  <span>بازه قبلی: <span dir="ltr">{data.previous_from?.slice(0, 10)} تا {data.previous_to?.slice(0, 10)}</span></span>
                </div>
              )}
            </div>

            {error && <Alert type="error" showIcon message={<span className="custom-font">{error}</span>} />}

            {/* هشدار تماس‌های پرریسک */}
            {data?.insights?.high_risk_needs_review > 0 && (
              <Alert
                type="error"
                showIcon
                icon={<WarningOutlined />}
                className="custom-font"
                message={(
                  <span className="custom-font font-bold">
                    🚨 {Number(data.insights.high_risk_needs_review).toLocaleString('fa-IR')} تماس نیازمند بررسی فوری
                  </span>
                )}
                description={<span className="custom-font">تماس‌هایی که هوش مصنوعی آن‌ها را پرریسک تشخیص داده (مشتری عصبانی، شکایت، درخواست لغو و ...) و هنوز بررسی نشده‌اند.</span>}
                action={(
                  <Button size="small" danger type="primary" onClick={() => navigate('/call-center/calls?risk=needs_review')} className="custom-font">
                    مشاهده تماس‌ها
                  </Button>
                )}
              />
            )}

            {/* هشدار فرصت‌های فروش ازدست‌رفته */}
            {data?.insights?.missed_opportunity_needs_review > 0 && (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                className="custom-font"
                message={(
                  <span className="custom-font font-bold">
                    💸 {Number(data.insights.missed_opportunity_needs_review).toLocaleString('fa-IR')} فرصت فروش ازدست‌رفته نیازمند پیگیری
                  </span>
                )}
                description={(
                  <span className="custom-font">
                    تماس‌هایی که مشتری سیگنال خرید داده (مثلاً درخواست لینک پرداخت) اما اقدام بعدی مناسبی انجام یا مشخص نشده است.
                    {data.insights.missed_opportunity_total > data.insights.missed_opportunity_needs_review
                      ? ` (جمع کل بازه: ${Number(data.insights.missed_opportunity_total).toLocaleString('fa-IR')})`
                      : ''}
                  </span>
                )}
                action={(
                  <Button size="small" type="primary" onClick={() => navigate('/call-center/calls?risk=missed_review')} className="custom-font">
                    مشاهده تماس‌ها
                  </Button>
                )}
              />
            )}

            {/* کارت‌های KPI */}
            {loading && !data ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                {KPI_CONFIG.map((c) => (
                  <div key={c.key} className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-4">
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                ))}
              </div>
            ) : data && !hasCalls ? (
              <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-8">
                <Empty description={<span className="custom-font">در این بازه تماسی ثبت نشده است</span>} />
              </div>
            ) : data ? (
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {KPI_CONFIG.map((config) => (
                  <KpiCard key={config.key} config={config} kpi={data.kpis?.[config.key]} />
                ))}
              </div>
            ) : null}

            <HourlyHeatmapSection from={apiFrom} to={apiTo} />

            <RepeatCallersSection from={apiFrom} to={apiTo} />

            {/* پرتکرارترین اعتراض‌ها */}
            {data && hasCalls && (
              <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-1">
                  <StopOutlined className="text-amber-500" />
                  <h2 className="custom-font text-sm font-bold text-gray-800 dark:text-gray-100 m-0">اعتراض‌های مشتریان</h2>
                </div>
                {data.insights?.top_objections?.length > 0 ? (
                  <>
                    <div className="custom-font text-sm text-gray-600 dark:text-gray-300 mb-3">
                      پرتکرارترین اعتراض در این بازه: <b>{data.insights.top_objections[0].name}</b>
                      {' — '}
                      <span dir="ltr">{formatDecimal(data.insights.top_objections[0].percent)}%</span>
                      <span className="text-xs text-gray-400"> (از {Number(data.insights.calls_with_objection).toLocaleString('fa-IR')} تماس دارای اعتراض)</span>
                    </div>
                    {(() => {
                      const rising = data.insights.top_objections.filter((o) => o.trend === 'up' || o.trend === 'new');
                      if (rising.length === 0) return null;
                      return (
                        <div className="custom-font text-xs rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 px-3 py-2 mb-3">
                          📈 روند صعودی نسبت به بازه قبل:{' '}
                          {rising.map((o, i) => (
                            <span key={o.id}>
                              {i > 0 ? '، ' : ''}
                              <b>{o.name}</b> ({Number(o.count).toLocaleString('fa-IR')} تماس{o.trend === 'new' ? '، جدید' : ` از ${Number(o.previous_count).toLocaleString('fa-IR')}`})
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="space-y-2">
                      {data.insights.top_objections.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 min-w-0">
                          <div className="w-36 sm:w-44 shrink-0 min-w-0"><CallSubjectChip subject={item} size="small" /></div>
                          <Progress
                            percent={item.percent ?? 0}
                            showInfo={false}
                            strokeColor={item.color}
                            size="small"
                            className="flex-1 m-0"
                          />
                          <span className="custom-font text-xs text-gray-500 w-28 text-left shrink-0" dir="ltr">
                            {Number(item.count).toLocaleString('fa-IR')} تماس · {formatDecimal(item.percent)}%
                          </span>
                          <Tooltip title={`بازه قبل: ${Number(item.previous_count).toLocaleString('fa-IR')} تماس`}>
                            <span
                              className={`custom-font text-xs w-14 shrink-0 text-center ${
                                item.trend === 'up' || item.trend === 'new' ? 'text-red-500' : item.trend === 'down' ? 'text-green-600' : 'text-gray-400'
                              }`}
                            >
                              {item.trend === 'new' ? 'جدید' : item.trend === 'up' ? '▲' : item.trend === 'down' ? '▼' : '—'}
                              {item.trend === 'up' || item.trend === 'down' ? ` ${Math.abs(item.count - item.previous_count).toLocaleString('fa-IR')}` : ''}
                            </span>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="custom-font text-sm text-gray-400">
                    در این بازه اعتراضی ثبت نشده است. (اعتراض‌ها پس از تحلیل تماس‌ها با هوش مصنوعی ثبت می‌شوند.)
                  </div>
                )}
              </div>
            )}

            {data && hasCalls && (
              <div className="text-[11px] text-gray-400 custom-font flex items-center gap-1">
                <BarChartOutlined />
                مقدار «بدون مقایسه» یعنی در بازه قبل داده‌ای برای محاسبه آن شاخص وجود نداشته است.
              </div>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};


export default CallCenterDashboard;
